import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, MapPin, Phone, Clock, MessageCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function BranchPickerModal({ open, mode, course, onClose, onSelect }) {
  const [branches, setBranches] = useState([]);

  useEffect(() => {
    if (!open) return;
    api
      .get("/branches")
      .then((r) => {
        let list = r.data;
        // Filter unavailable branches if a course is provided
        if (course && course.prices) {
          list = list.filter(
            (b) => course.prices[b.id] !== "no_disponible"
          );
        }
        setBranches(list);
      })
      .catch(() => {});
  }, [open, course]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const isWA = mode === "whatsapp";
  const heading = isWA ? "Hablemos por WhatsApp" : "Elige tu sucursal";
  const subline = course
    ? isWA
      ? `Selecciona la sucursal más cercana para chatear sobre el curso de ${course.title}.`
      : `Selecciona la sucursal en la que te gustaría inscribirte al curso de ${course.title}.`
    : isWA
    ? "Selecciona la sucursal más cercana para chatear con nuestro equipo."
    : "Selecciona la sucursal más cercana.";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.25 }}
          className="fixed inset-0 z-[110] flex items-end md:items-center justify-center"
          data-testid="branch-picker-overlay"
        >
          <button
            aria-label="Cerrar"
            data-testid="branch-picker-backdrop"
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm cursor-default"
          />

          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="branch-picker-title"
            initial={{ opacity: 0, y: 40, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.98 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative bg-white w-full md:w-[94%] md:max-w-5xl max-h-[92dvh] md:max-h-[92vh] flex flex-col overflow-hidden shadow-[0_40px_80px_-20px_rgba(0,0,0,0.45)]"
            data-testid="branch-picker"
          >
            <div className="overflow-y-auto flex-1 relative">
            <div className="sticky top-0 z-30 flex justify-end pointer-events-none">
              <button
                onClick={onClose}
                aria-label="Cerrar"
                data-testid="branch-picker-close"
                className="pointer-events-auto m-2 md:m-3 w-10 h-10 flex items-center justify-center bg-white text-[#0a0a0a] border border-black/10 hover:bg-[#0a0a0a] hover:text-white hover:border-[#0a0a0a] transition-colors shadow-md"
              >
                <X size={18} strokeWidth={1.6} />
              </button>
            </div>
            <div className="-mt-12 px-7 pt-2 pb-7 md:px-10 md:pt-4 md:pb-10 lg:px-12 lg:pb-12">
              <div className="flex items-center gap-3 mb-4">
                <span className="dot-red" />
                <span className="overline">
                  6 sucursales · Puebla
                </span>
              </div>
              <h3
                id="branch-picker-title"
                data-testid="branch-picker-title"
                className="font-display font-semibold tracking-tighter text-3xl md:text-4xl text-[#0a0a0a] leading-tight"
              >
                {heading}
              </h3>
              <p className="mt-3 text-sm md:text-base font-light text-[#525252] max-w-2xl leading-relaxed">
                {subline}
              </p>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {branches.map((b, i) => (
                  <motion.button
                    key={b.id}
                    type="button"
                    data-testid={`branch-pick-${b.id}`}
                    onClick={() => onSelect(b)}
                    initial={{ opacity: 0, y: 14 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4, delay: i * 0.05 }}
                    className="group text-left bg-white border border-black/8 p-5 hover:border-[#c40000] hover:shadow-[0_20px_40px_-20px_rgba(196,0,0,0.45)] hover:-translate-y-0.5 transition-all"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="font-display font-semibold text-lg tracking-tight text-[#0a0a0a]">
                        {b.name}
                      </div>
                      <span
                        className={`shrink-0 w-9 h-9 flex items-center justify-center border transition-colors ${
                          isWA
                            ? "border-[#25d366]/40 text-[#25d366] group-hover:bg-[#25d366] group-hover:text-white group-hover:border-[#25d366]"
                            : "border-black/15 text-[#0a0a0a] group-hover:bg-[#0a0a0a] group-hover:text-white group-hover:border-[#0a0a0a]"
                        }`}
                      >
                        {isWA ? (
                          <MessageCircle size={15} strokeWidth={1.6} />
                        ) : (
                          <ArrowRight size={15} strokeWidth={1.6} />
                        )}
                      </span>
                    </div>
                    <div className="mt-4 space-y-2 text-xs text-[#525252]">
                      <p className="flex items-start gap-2">
                        <MapPin
                          size={13}
                          strokeWidth={1.5}
                          className="mt-0.5 shrink-0"
                        />
                        <span>{b.address}</span>
                      </p>
                      <p className="flex items-center gap-2">
                        <Phone size={13} strokeWidth={1.5} /> {b.phone}
                      </p>
                      <p className="flex items-center gap-2">
                        <Clock size={13} strokeWidth={1.5} /> {b.hours}
                      </p>
                    </div>
                    <div className="mt-4 pt-3 border-t border-black/8 text-[0.65rem] uppercase tracking-[0.2em] font-semibold text-[#0a0a0a] group-hover:text-[#c40000] transition-colors">
                      {isWA ? "Chatear con esta sucursal" : "Inscribirme aquí"}
                    </div>
                  </motion.button>
                ))}
              </div>
            </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
